import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box, Card, CardContent, Typography, Button, Avatar, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import useScrollToTop from '../hooks/useScrollToTop';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  margin: '0 auto 16px',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontSize: '2rem',
  fontWeight: 'bold',
}));

const StyledTypography = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
}));

const About: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const teamMembers = [
    {
      name: t('team.jingningLu.name'),
      initials: 'JL',
      title: t('team.jingningLu.title'),
      bio: t('team.jingningLu.minibio'),
      link: '/team/jingning-lu'
    },
    {
      name: t('team.jiaruiYin.name'),
      initials: 'JY',
      title: t('team.jiaruiYin.title'),
      bio: t('team.jiaruiYin.minibio'),
      link: '/team/jiarui-yin'
    },
    {
      name: t('team.yanbingLu.name'),
      initials: 'YL',
      title: t('team.yanbingLu.title'),
      bio: t('team.yanbingLu.minibio'),
      link: '/team/yanbing-lu'
    },
    {
      name: t('team.brianMcGrane.name'),
      initials: 'BM',
      title: t('team.brianMcGrane.title'),
      bio: t('team.brianMcGrane.minibio'),
      link: '/team/brian-mcgrane'
    },
    {
      name: t('team.crystalBraveboyChetram.name'),
      initials: 'CB',
      title: t('team.crystalBraveboyChetram.title'),
      bio: t('team.crystalBraveboyChetram.minibio'),
      link: '/team/crystal-braveboy-chetram'
    },
    {
      name: t('team.denizEdaCardak.name'),
      initials: 'DC',
      title: t('team.denizEdaCardak.title'),
      bio: t('team.denizEdaCardak.minibio'),
      link: '/team/deniz-eda-cardak'
    },
    {
      name: t('team.matiasApparcel.name'),
      initials: 'MA',
      title: t('team.matiasApparcel.title'),
      bio: t('team.matiasApparcel.minibio'),
      link: '/team/matias-apparcel'
    },
    {
      name: t('team.mischaMannixOpie.name'),
      initials: 'MM',
      title: t('team.mischaMannixOpie.title'),
      bio: t('team.mischaMannixOpie.minibio'),
      link: '/team/mischa-mannix-opie'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'grey.50',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: 'white',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{ 
          maxWidth: '7xl', 
          width: '100%',
          py: 8, 
          px: { xs: 4, sm: 6, lg: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: '3xl' }}>
              <StyledTypography variant="h2" component="h1" gutterBottom>
                {t('about.title')}
              </StyledTypography>
              <StyledTypography variant="h5" color="text.secondary" sx={{ mt: 3 }}>
                {t('about.subtitle')}
              </StyledTypography>
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Story Section */}
      <Box sx={{ 
        maxWidth: '7xl', 
        width: '100%',
        py: 8, 
        px: { xs: 4, sm: 6, lg: 8 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: '3xl' }}>
            <StyledTypography variant="h3" component="h2" gutterBottom>
              {t('about.story.title')}
            </StyledTypography>
            <Box sx={{ mt: 6 }}>
              <StyledTypography variant="body1" color="text.secondary" paragraph>
                {t('about.story.paragraph1')}
              </StyledTypography>
              <StyledTypography variant="body1" color="text.secondary">
                {t('about.story.paragraph2')}
              </StyledTypography>
            </Box>
          </Box>
        </motion.div>
      </Box>

      {/* Team Section */}
      <Box sx={{ 
        bgcolor: 'white',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{ 
          maxWidth: '7xl', 
          width: '100%',
          py: 8, 
          px: { xs: 4, sm: 6, lg: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: '3xl' }}>
              <StyledTypography variant="h3" component="h2" gutterBottom>
                {t('about.team.title')}
              </StyledTypography>
              <StyledTypography variant="h6" color="text.secondary" sx={{ mt: 4 }}>
                {t('about.team.subtitle')}
              </StyledTypography>
            </Box>
          </motion.div>

          <Box sx={{ 
            mt: 12,
            width: '100%',
            display: 'grid',
            gap: 4,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)'
            },
            maxWidth: '7xl'
          }}>
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <StyledAvatar>{member.initials}</StyledAvatar>
                    <StyledTypography variant="h6" component="h3" gutterBottom>
                      {member.name}
                    </StyledTypography>
                    <StyledTypography variant="subtitle1" color="text.secondary" gutterBottom>
                      {member.title}
                    </StyledTypography>
                    <StyledTypography variant="body2" color="text.secondary" paragraph>
                      {member.bio}
                    </StyledTypography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        component={Link}
                        to={member.link}
                        variant="contained"
                        color="primary"
                      >
                        {t('about.team.moreInfo')}
                      </Button>
                    </Box>
                  </CardContent>
                </StyledCard>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default About; 